import React, { PureComponent } from 'react';

import flow from 'lodash/flow';
import { withBrowserInfo } from '../../hocs';
import { getSearchParamsFromLocation } from '../CategoryPage/provider/location';
import resolver from '../../state/resolvers/event';
import { SEGMENT_EVENTS } from '../../utils/tracking';
import { smoothScrollOnElement } from '../../utils/smoothScroll';
import CategoryMapPage from './component';
import Loading from '../../shared/Loading';
import {
  withLocationInfo,
  withTracking,
  withSearchInfo,
} from '../CategoryPage/provider';

import { dataLoadingStyle } from './styles.module.css';

const MOBILE_EVENT_LIST_MARGIN = 10;

const getQuery = (data) => data?.categoryData?.query;

const getCategoryEvents = ({ categoryData } = {}) =>
  (categoryData?.eventsV2 || categoryData?.events || {})?.events;
const getCategoryNearbyEvents = ({ categoryData } = {}) =>
  (categoryData?.eventsV2 || categoryData?.events || {})?.nearbyEvents;

const getMapEvents = (events) =>
  events?.map?.((event) => ({
    ...resolver(event),
    coordinates: [event.location.long, event.location.lat],
  }));

const getHeaderText = (searchParams, categoryData = {}) => {
  const { name: textSearchTerm } = searchParams;
  const {
    content: { pageInfo: { title: pageTitle } = {} } = {},
  } = categoryData;

  return textSearchTerm ? `Results for '${textSearchTerm}'` : pageTitle;
};

class CategoryMapPageContainer extends PureComponent {
  // eslint-disable-next-line react/state-in-constructor
  state = {
    selectedEventId: undefined,
    hoverEventId: undefined,
    viewport: undefined,
  };

  selectedEventCardRef = React.createRef();

  eventListRef = React.createRef();

  onEventHoverOn = (hoverEventId) => {
    this.setState({ hoverEventId });
  };

  onEventHoverOff = () => {
    this.setState({ hoverEventId: undefined });
  };

  handleTracking = (name, params, segmentEvent) => {
    const {
      injected: {
        tracking: { trackButtonClick, trackMapMoved },
      },
    } = this.props;

    switch (segmentEvent) {
      case SEGMENT_EVENTS.MAP_MOVED:
        trackMapMoved(name, { ...params });
        break;
      default:
        trackButtonClick(name, { ...params });
        break;
    }
  };

  handleEventSelect = (selectedEventId, indexInEventList, _coordinates) => {
    if (!selectedEventId) return this.setState({ selectedEventId: undefined });

    const [lng, lat] = _coordinates;
    const coordinates = { lat, lng };
    this.handleTracking('pin_selected', {
      selectedEventId,
      indexInEventList,
      coordinates,
    });
    this.setState({ selectedEventId }, this.scrollToSelectedEvent);
  };

  scrollToSelectedEvent = () => {
    const {
      eventListRef: { current: eventListElem } = {},
      selectedEventCardRef: { current: selectedEventCardElem } = {},
    } = this;
    if (!eventListElem || !selectedEventCardElem) return;

    smoothScrollOnElement(eventListElem, {
      top: selectedEventCardElem.offsetTop,
      left: selectedEventCardElem.offsetLeft - MOBILE_EVENT_LIST_MARGIN,
      behavior: 'smooth',
    });
  };

  handleNewSearch = ({
    viewportNorthEastLat,
    viewportNorthEastLong,
    viewportSouthWestLat,
    viewportSouthWestLong,
  }) => {
    const {
      injected: {
        location: { setViewport },
      },
      isExternalMap,
    } = this.props;

    this.handleTracking('search_this_area', {
      viewportNorthEastLat,
      viewportNorthEastLong,
      viewportSouthWestLat,
      viewportSouthWestLong,
    });

    const viewport = {
      ne: {
        lat: viewportNorthEastLat,
        lng: viewportNorthEastLong,
      },
      sw: {
        lat: viewportSouthWestLat,
        lng: viewportSouthWestLong,
      },
    };

    this.setState({ viewport, selectedEventId: undefined });
    if (isExternalMap) return setViewport('Custom Location', viewport);

    setViewport('Custom Location', viewport, {
      map: true,
    });
  };

  handleBackClick = () => {
    const { router, updateSearchQueryString } = this.props;
    const {
      query: { map, ...query },
    } = router.getCurrentLocation();

    this.handleTracking('back_to_search');

    const loc = this.state.viewport
      ? getSearchParamsFromLocation({
          name: 'Custom Location',
          viewport: this.state.viewport,
        })
      : {};

    updateSearchQueryString({ ...query, ...loc, map: undefined });
  };

  render() {
    const {
      data,
      searchParams,
      isMobile,
      hideBackClick,
      hideEventList,
      mapStyle,
      injected: {
        location: { point, viewport },
      },
    } = this.props;

    if (!data) return null;
    const { selectedEventId, hoverEventId } = this.state;

    const specificEvents = getCategoryEvents(data);
    const nearbyEvents = getCategoryNearbyEvents(data);
    const events = specificEvents?.concat(nearbyEvents);

    if (!(point || viewport) || !events) {
      return (
        <div className={dataLoadingStyle}>
          <Loading />
        </div>
      );
    }

    const componentProps = {
      categoryData: data?.categoryData,
      query: getQuery(data),
      point,
      viewport,
      selectedEventId,
      hoverEventId,
      mapEvents: getMapEvents(events),
      specificEvents: getMapEvents(specificEvents),
      nearbyEvents: getMapEvents(nearbyEvents),
      loading: data?.loading,
      onEventSelect: this.handleEventSelect,
      eventListRef: this.eventListRef,
      selectedEventCardRef: this.selectedEventCardRef,
      onNewSearch: this.handleNewSearch,
      backText: getHeaderText(searchParams, data.categoryData),
      onBackClick: this.handleBackClick,
      showHeader: isMobile,
      handleTracking: this.handleTracking,
      onEventHoverOn: this.onEventHoverOn,
      onEventHoverOff: this.onEventHoverOff,
      hideBackClick,
      hideEventList,
      mapStyle,
    };

    return <CategoryMapPage {...componentProps} {...this.props} />;
  }
}

CategoryMapPageContainer.defaultProps = {
  injected: {
    location: {
      setViewport: () => {},
    },
  },
};

export default flow(
  withBrowserInfo,
  withLocationInfo,
  withSearchInfo,
  withTracking('map'),
)(CategoryMapPageContainer);
